import React, { useState, useEffect, useRef } from 'react';
import { CheckboxFacetUI, type CheckboxFacetUIItem } from './CheckboxFacetUI';
import { useGoogleSearchContext } from '../../../components/search/GoogleInstantSearch';

type GoogleCheckboxRefinementProps = {
  facet: string;
  setDisableScroll: (v: boolean) => void;
  onDropdownToggle?: (isOpen: boolean) => void;
};

const GoogleCheckboxRefinement = ({ facet, setDisableScroll, onDropdownToggle }: GoogleCheckboxRefinementProps) => {
  const { facets, state, setFacet } = useGoogleSearchContext();

  const facetKey = Object.keys(facets).find(
    k => k.toLowerCase() === facet.toLowerCase() || k.toLowerCase() === `facets.${facet.toLowerCase()}`
  );
  const valueMap: Record<string, number> = (facetKey ? facets[facetKey] : undefined) ?? {};

  const items: Array<CheckboxFacetUIItem> = Object.entries(valueMap).map(([label, count]) => ({
    label,
    count,
    isRefined: state.facets.some(f => f.name === facet && f.value === label),
    value: label,
  }));

  const [showCheckboxInput, setShowCheckboxInput] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const refineButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const checkedItems = items.filter(i => i.isRefined);
  const isSomeRefined = checkedItems.length;

  const normalizedFilter = filterSearchTerm.trim().toLowerCase();
  const checkboxItems = filterSearchTerm
    ? items.filter(i => i.label.toLowerCase().startsWith(normalizedFilter))
    : [...items].sort((a, b) => {
        const refinedDiff = (b.isRefined ? 1 : 0) - (a.isRefined ? 1 : 0);
        if (refinedDiff !== 0) return refinedDiff;
        return b.count - a.count;
      });

  useEffect(() => {
    if (showCheckboxInput) {
      const handler = (event: PointerEvent) => {
        const btn = refineButtonRef.current;
        const dd = dropdownRef.current;
        if (!event || (dd && !dd.contains(event.target as Node) && btn && !btn.contains(event.target as Node))) {
          setShowCheckboxInput(false);
          setDisableScroll(false);
        }
      };
      document.body.addEventListener('pointerdown', handler);
      setDisableScroll(true);
      return () => {
        document.body.removeEventListener('pointerdown', handler);
      };
    } else {
      setDisableScroll(false);
    }
  }, [showCheckboxInput, setDisableScroll]);

  const onToggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    onDropdownToggle?.(!showCheckboxInput);
    setFilterSearchTerm('');
    setShowCheckboxInput(v => !v);
  };

  const onToggleCheck = (_e: React.ChangeEvent<HTMLInputElement>, item: CheckboxFacetUIItem) => {
    setFacet(facet, item.label, !item.isRefined);
  };

  const onRemoveTag = (_e: React.SyntheticEvent, label: string) => {
    setFacet(facet, label, false);
  };

  return (
    <CheckboxFacetUI
      facet={facet}
      items={items}
      checkboxItems={checkboxItems}
      checkedItems={checkedItems}
      filterSearchTerm={filterSearchTerm}
      showCheckboxInput={showCheckboxInput}
      isSomeRefined={isSomeRefined}
      refineButtonRef={refineButtonRef}
      dropdownRef={dropdownRef}
      onToggleDropdown={onToggleDropdown}
      onFilterSearchChange={e => setFilterSearchTerm(e.target.value)}
      onToggleCheck={onToggleCheck}
      onLabelClick={() => {}}
      onRemoveTag={onRemoveTag}
    />
  );
};

export default GoogleCheckboxRefinement;
